import { type ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { InputGroup, InputGroupInput } from '@/components/Input/InputGroup';
import { cn } from '@/util/utils';

type InputProps = Omit<ComponentProps<'input'>, 'size'> &
    VariantProps<typeof inputVariants> & {
        /** Filled from another record: read-only, muted, with the padlock (see InputGroup). */
        locked?: boolean;
    };

/**
 * ui-input, READ from the design system's Input (65:533): card ground, 1px base/input
 * border, px 12, 36 high, rounded-md, shadow/xs, 14px text. Focus moves the border to the
 * ring colour and adds the 3px glow instead of an outline; an invalid field turns its border
 * destructive and its glow destructive. The login form's fields are the 32px `sm` size.
 */
function Input({ className, type, inputSize, locked = false, ref, ...props }: InputProps) {
    if (locked) {
        // The padlock needs a container, so a locked field is the group with the field inside.
        return (
            <InputGroup locked className={cn(inputSize === 'sm' && 'h-8', className)}>
                <InputGroupInput
                    data-slot="input"
                    type={type}
                    tabIndex={-1}
                    ref={ref}
                    {...props}
                    readOnly
                />
            </InputGroup>
        );
    }
    return (
        <input
            type={type}
            ref={ref}
            data-slot="input"
            className={cn(inputVariants({ inputSize }), className)}
            {...props}
        />
    );
}

const inputVariants = cva(
    [
        'pointer-coarse:min-h-11 pointer-coarse:text-base bg-card text-foreground border-input w-full min-w-0 rounded-md border px-3 text-sm shadow-xs',
        'placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground',
        'transition-[color,box-shadow,border-color] duration-(--duration-fast) outline-none',
        'focus-visible:border-ring focus-visible:shadow-[0_0_0_3px_var(--outline)]',
        'aria-invalid:border-destructive aria-invalid:focus-visible:shadow-[0_0_0_3px_var(--outline-destructive)]',
        'disabled:bg-muted disabled:text-text-locked disabled:cursor-not-allowed disabled:shadow-none',
        'file:text-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
    ],
    {
        variants: {
            inputSize: {
                default: 'h-9',
                sm: 'h-8',
            },
        },
        defaultVariants: { inputSize: 'default' },
    },
);

export { Input, type InputProps };
